import { MutableRefObject } from 'react';
import { Options } from '@glidejs/glide';

import {
  CardCarouselProps,
  GlideInstance,
  glideTrackRefType,
  setCurrentIndexType,
} from './CardCarouselContainer.types';
import { canUseDOM } from '../../utils';
import { constants } from '../../constants';

const { TAB, ARROW_RIGHT, ARROW_LEFT, CARD_CAROUSEL, LEFT } = constants;

export const getActiveDotIndex = (cardIndex: number, cardCount: number, cardsPerView: number) => {
  const totalGroups = Math.ceil(cardCount / cardsPerView); // Total number of groups
  const currentGroup = cardIndex + cardsPerView >= cardCount ? totalGroups - 1 : Math.floor(cardIndex / cardsPerView);
  // If we're on the last group and the remaining cards are fewer than `cardsPerView`,
  // make sure the last dot is active.
  if (currentGroup === totalGroups - 1 && cardCount % cardsPerView !== 0) {
    return totalGroups - 1; // Activate the last dot
  }

  return currentGroup; // Otherwise, return the calculated group index
};

export const glideOptions: Options = {
  type: CARD_CAROUSEL.TYPE,
  bound: true,
  gap: 16,
  dragThreshold: false,
  swipeThreshold: false,
  keyboard: false,
};

export const calculatePerView = (
  cardWidth: number | string,
  noOfCards: number,
  glideRef: MutableRefObject<HTMLDivElement | null>
) => {
  let cardWidthInPx: number = 0;
  const containerWidth = glideRef?.current?.offsetWidth || 0;

  if (typeof cardWidth === 'string' && cardWidth.endsWith('%')) {
    const percentage = parseFloat(cardWidth);
    cardWidthInPx = (percentage / 100) * containerWidth;
  } else {
    cardWidthInPx = typeof cardWidth === 'number' ? cardWidth : parseFloat(cardWidth);
  }

  const perView = cardWidthInPx > 0 ? Math.max(1, Math.floor(containerWidth / cardWidthInPx)) : noOfCards;
  return { perView, cardWidthInPx };
};

export const updateTransform = (
  glideTrackRef: glideTrackRefType,
  index: number,
  activeIndex: number,
  cardWidth: number,
  cardsPerView: number,
  cardCount: number,
  isTransform: boolean = false
) => {
  let transformIndex = index;
  if (transformIndex + cardsPerView >= cardCount) transformIndex = cardCount - cardsPerView;
  if (transformIndex < 0) transformIndex = 0;
  const transformValue = `translate3d(${-transformIndex * (cardWidth + 16)}px, 0px, 0px)`;

  if (glideTrackRef.current) {
    if (isTransform || cardsPerView > 2) {
      glideTrackRef.current.style.transition = CARD_CAROUSEL.TRANSITION;
      glideTrackRef.current.style.transform = transformValue;
    }

    // Set focus to the first card when a new slide is active
    if (activeIndex > -1) {
      const activeSlide = glideTrackRef.current?.childNodes[activeIndex];
      if (activeSlide) (activeSlide as HTMLElement).focus();
    }
  }
};

export const calculateNewIndex = (
  direction: string,
  currentIndex: number,
  cardCount: number,
  cardsPerView: number,
  isImageCarousel?: boolean
) => {
  let newIndex = currentIndex;
  if (isImageCarousel) {
    if (direction === LEFT) {
      newIndex = newIndex > 0 ? newIndex - 1 : cardCount - 1;
    } else {
      newIndex = newIndex + 1 >= cardCount ? 0 : newIndex + 1;
    }
  } else {
    const totalGroups = Math.ceil(cardCount / cardsPerView); // Total number of groups
    const currentGroup =
      currentIndex + cardsPerView >= cardCount ? totalGroups - 1 : Math.floor(currentIndex / cardsPerView);

    if (direction === LEFT) {
      newIndex = currentGroup > 0 ? (currentGroup - 1) * cardsPerView : 0;
    } else {
      newIndex =
        currentGroup < totalGroups - 1 ? (currentGroup + 1) * cardsPerView : Math.max(0, cardCount - cardsPerView);
      if (newIndex + cardsPerView >= cardCount) newIndex = Math.max(0, cardCount - cardsPerView);
    }
  }
  return newIndex;
};

export const handleKeyboardNavigation = (
  e: KeyboardEvent,
  glideInstance: GlideInstance | null,
  cardCount: number,
  isKeyboardMove: MutableRefObject<boolean>,
  handleArrowClick: (e: React.SyntheticEvent | KeyboardEvent, direction: string) => void,
  isImageCarousel?: boolean
) => {
  if (glideInstance) {
    // Set the flag to true before triggering the move
    if (e.key === TAB) {
      isKeyboardMove.current = true;
    }
    if (
      (e.key === ARROW_RIGHT && (isImageCarousel || glideInstance.index < cardCount - 1)) ||
      (e.key === ARROW_LEFT && (isImageCarousel || glideInstance.index > 0))
    ) {
      e.preventDefault();
      isKeyboardMove.current = true;
      handleArrowClick(e, e.key === ARROW_RIGHT ? 'right' : 'left');
    }
  }
};

export const handleFocusOnCard = (
  cardWidth: number,
  cardCount: number,
  currentIndex: number,
  cardsPerView: number,
  glideInstance: GlideInstance,
  setCurrentIndex: setCurrentIndexType,
  glideTrackRef: glideTrackRefType
) => {
  if (canUseDOM) {
    const activeElement = document.activeElement as HTMLElement;
    const focusedCard = activeElement.closest('.glide__slides li'); // Find the closest card element
    if (focusedCard && glideTrackRef.current) {
      const focusedCardIndex = Array.from(glideTrackRef.current.children).indexOf(focusedCard);

      // If focus moved to a different sibling card
      if (focusedCardIndex !== currentIndex) {
        setCurrentIndex(focusedCardIndex);
        try {
          glideInstance.index = focusedCardIndex;
          glideInstance.update({});
        } catch (error) {
          console.log('Failed to update Glide instance:', error);
        }
        updateTransform(glideTrackRef, focusedCardIndex, -1, cardWidth, cardsPerView, cardCount, true);
      }
    }
  }
};

export const handleResize = (
  carouselProps: CardCarouselProps,
  cardsPerView: number,
  glideInstance: GlideInstance,
  glideRef: MutableRefObject<HTMLDivElement | null>,
  glideTrackRef: glideTrackRefType,
  setCurrentIndex: setCurrentIndexType
) => {
  const { cardWidth, noOfCards = 4, cardCount, showMiniArrows } = carouselProps;
  const { perView: newPerView, cardWidthInPx } = calculatePerView(cardWidth, noOfCards, glideRef);
  if (newPerView !== cardsPerView) {
    setCurrentIndex(prevState => {
      const newIndex = Math.max(0, Math.min(prevState - (newPerView - cardsPerView), cardCount - newPerView));
      glideInstance.index = newIndex;
      glideInstance.update({ perView: newPerView });
      if (showMiniArrows)
        updateTransform(glideTrackRef, newIndex, newIndex, cardWidthInPx, cardsPerView, cardCount, true);
      return newIndex;
    });
  }
};

export const transformCards = (
  currentTransformValue: string,
  currentIndex: number,
  initialCardsPerView: number,
  cardCount: number,
  cardWidth: number,
  glideTrackRef: glideTrackRefType
) => {
  if (currentTransformValue && glideTrackRef.current) {
    let transformIndex = currentIndex;
    if (currentIndex + initialCardsPerView >= cardCount) {
      transformIndex = cardCount - initialCardsPerView;
      if (transformIndex < 0) transformIndex = 0;
    }
    const newTransformValue = `translate3d(${-transformIndex * (cardWidth + 16)}px, 0px, 0px)`;
    if (currentTransformValue !== newTransformValue) {
      glideTrackRef.current.style.transform = newTransformValue;
    }
  }
};
